import React, { useState, useEffect, useMemo } from 'react';
import styles from './StandardPage.module.css';
import { PageFrame, PageHeader, FilterBar, Button, Icon } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import './utils/cell-states.css';
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { BrokerWiseBrokerageRequest, BrokerWiseBrokerageData } from '@/types/client';
import { brokerWiseBrokerageApi, brokerMasterApi } from '@/services/api';

type SortColumn = keyof BrokerWiseBrokerageData | 'diff_pct';

const BrokerWiseBrokerage: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<BrokerWiseBrokerageData[]>([]);
  const [grandTotalBrokerage, setGrandTotalBrokerage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('diff_pct');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Draft filter state
  const [pendingFromDate, setPendingFromDate] = useState<string>(fromDate);
  const [pendingToDate, setPendingToDate] = useState<string>(toDate);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBroker, setEditingBroker] = useState<BrokerWiseBrokerageData | null>(null);
  const [editTargetPct, setEditTargetPct] = useState<string>('');
  const [editTargetAmt, setEditTargetAmt] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const hasChanges = pendingFromDate !== fromDate || pendingToDate !== toDate;
  const isInvalidFilters = !pendingFromDate || !pendingToDate || pendingFromDate > pendingToDate;

  const applyFilters = () => {
    if (isInvalidFilters) return;
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
  };

  const fetchData = async () => {
    if (!fromDate || !toDate || fromDate > toDate) return;

    setLoading(true);
    setError(null);

    try {
      const ordering = sortColumn === 'diff_pct'
        ? undefined
        : `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`;
      const params: BrokerWiseBrokerageRequest & { ordering?: string } = { from_date: fromDate, to_date: toDate };
      if (ordering) params.ordering = ordering;
      const response = await brokerWiseBrokerageApi.getBrokerWiseBrokerage(params);

      if (response.status >= 400) {
        throw new Error(response.statusText);
      }

      const rows: BrokerWiseBrokerageData[] = response.data?.data?.broker_wise_brokerage_data || [];
      setData(rows);
      setGrandTotalBrokerage(response.data?.meta?.grand_total_brokerage || 0);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: unknown } };
      const errorMessage = axiosErr.response?.data ? JSON.stringify(axiosErr.response.data) : String(err) || 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when date range changes
  useEffect(() => {
    fetchData();
  }, [fromDate, toDate]);

  // Fetch data when server-side sort changes
  useEffect(() => {
    if (sortColumn !== 'diff_pct') {
      fetchData();
    }
  }, [sortColumn, sortDirection]);

  const handleEdit = (broker: BrokerWiseBrokerageData) => {
    setEditingBroker(broker);
    setEditTargetPct(String(broker.target_pct));
    setEditTargetAmt(String(broker.target_amt));
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingBroker) return;
    setSaving(true);
    try {
      await brokerMasterApi.partialUpdate(editingBroker.id, {
        target_pct: editTargetPct,
        target_amt: editTargetAmt,
      });
      setEditDialogOpen(false);
      setEditingBroker(null);
      fetchData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: unknown } };
      const errorMessage = axiosErr.response?.data ? JSON.stringify(axiosErr.response.data) : String(err) || 'Failed to save';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatNumber = (value: number, decimals = 2): string => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const toLakhs = (value: number): number => value / 100000;

  const sortedData = useMemo(() => {
    return sortColumn === 'diff_pct'
      ? [...data].sort((brokerA, brokerB) => {
          const diffA = Number(brokerA.by_pct) - Number(brokerA.target_pct);
          const diffB = Number(brokerB.by_pct) - Number(brokerB.target_pct);
          return sortDirection === 'desc' ? diffB - diffA : diffA - diffB;
        })
      : data;
  }, [data, sortColumn, sortDirection]);

  /* Columns drive the head, the sort arrows, the cells and the totals row.
     `renderFooter` is what produces "Grand Total" — a hand-written <tr> can
     drift out of step with the headers, which is the bug that pattern avoids. */
  const columns = useMemo<ColumnDef<BrokerWiseBrokerageData>[]>(() => [
    { key: 'broker_name', header: 'Broker Name', sortable: true, width: '220px',
      tooltip: 'Name of the broker',
      render: r => r.broker_name,
      renderFooter: () => 'Grand Total' },
    { key: 'sebi_code', header: 'SEBI Code', sortable: true, width: '150px',
      tooltip: 'SEBI registration code',
      render: r => r.sebi_code },
    { key: 'total_brokerage', header: 'Brokerage (Lakhs)', sortable: true, align: 'right', width: '170px',
      tooltip: 'Total brokerage paid to this broker, in lakhs',
      render: r => formatNumber(toLakhs(Number(r.total_brokerage))),
      renderFooter: () => formatNumber(toLakhs(grandTotalBrokerage)) },
    { key: 'by_pct', header: '% of Total', sortable: true, align: 'right', width: '130px',
      tooltip: 'Share of total brokerage',
      render: r => formatNumber(Number(r.by_pct)),
      renderFooter: () => '100.00' },
    { key: 'target_pct', header: 'Target (%)', sortable: true, align: 'right', width: '130px',
      tooltip: 'Target brokerage percentage',
      render: r => formatNumber(Number(r.target_pct)) },
    { key: 'target_amt', header: 'Target Amount', sortable: true, align: 'right', width: '160px',
      tooltip: 'Target brokerage amount',
      render: r => formatNumber(Number(r.target_amt), 0) },
    { key: 'diff_pct', header: 'Diff (%)', sortable: true, align: 'right', width: '130px',
      tooltip: 'Actual share minus target share',
      // On the cell, not a span inside it, so the tint fills the whole cell.
      cellClassName: r => {
        const diff = Number(r.by_pct) - Number(r.target_pct);
        return diff > 0 ? 'cell-positive' : diff < 0 ? 'cell-negative' : '';
      },
      render: r => {
        const diff = Number(r.by_pct) - Number(r.target_pct);
        return `${diff >= 0 ? '+' : ''}${formatNumber(diff)}`;
      } },
    { key: 'actions', header: 'Actions', width: '110px',
      render: r => (
        <Button size="sm" onClick={() => handleEdit(r)} title="Edit target">
          <Icon name="edit" size={14} /> Edit
        </Button>
      ) },
  ], [grandTotalBrokerage, handleEdit]);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Broker-Wise Brokerage"
          subtitle="Brokerage charged by each broker over the selected period."
        />
      }
    >
      <FilterBar
        hideSearch
        filters={
          <>

          <div className={styles['filter-group']}>
            <TextField
              type="date"
              label="From Date"
              variant="outlined"
              size="small"
              value={pendingFromDate}
              onChange={(e) => setPendingFromDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </div>

          <div className={styles['filter-group']}>
            <TextField
              type="date"
              label="To Date"
              variant="outlined"
              size="small"
              value={pendingToDate}
              onChange={(e) => setPendingToDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </div>

          <div className={styles['filter-actions']}>
            <Tooltip title={isInvalidFilters ? 'Please ensure From Date is not after To Date' : (!hasChanges ? 'No changes to apply' : 'Apply selected filters')}>
              <span>
                <Button
                  variant="primary"
                  onClick={applyFilters}
                  disabled={!hasChanges || isInvalidFilters}
                >
                  {loading ? 'Applying...' : 'Apply'}
                </Button>
              </span>
            </Tooltip>
          </div>
          </>
        }
      />

      <DataTable<BrokerWiseBrokerageData>
        embedded
        data={sortedData}
        columns={columns}
        rowKey={row => row.id}
        footerRows={sortedData}
        totalCount={sortedData.length}
        loading={loading}
        error={error}
        currentPage={1}
        totalPages={1}
        onPageChange={() => undefined}
        pageSize={sortedData.length || 1}
        sortField={sortColumn}
        sortDir={sortDirection}
        onSort={field => handleSort(field as SortColumn)}
        hideSearch
        loadingMessage="Loading broker-wise brokerage…"
        emptyMessage="No broker-wise brokerage data for the selected dates."
      />

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Target - {editingBroker?.broker_name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Target (%)"
            type="number"
            fullWidth
            margin="normal"
            value={editTargetPct}
            onChange={(e) => setEditTargetPct(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Target Amount"
            type="number"
            fullWidth
            margin="normal"
            value={editTargetAmt}
            onChange={(e) => setEditTargetAmt(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
};

export default BrokerWiseBrokerage;
